import { BUY, CASH_APPROACH, SELL, SHORT_SELL } from '../constants';
import type { Currency } from '../currencies/types';
import type { FinancialInstrument, Player } from '../players/types';
import type { Session } from '../sessions/types';
import type { UnderlyingAsset } from '../underlying_assets/types';
import { cashExchange } from '../utils';

export type SimplifiedOptionMaxContracts = {
  [BUY]: number;
  [SELL]: number;
  [SHORT_SELL]: number;
};

type Inputs = {
  instrument: FinancialInstrument;
  asset: UnderlyingAsset;
  session: Session;
  player: Player;
  assetsList: UnderlyingAsset[];
  currenciesList: Currency[];
};

const noContracts: SimplifiedOptionMaxContracts = {
  [BUY]: 0,
  [SELL]: 0,
  [SHORT_SELL]: 0,
};

const wholeContracts = (capacity: number, perContract: number) => {
  if (!Number.isFinite(capacity) || capacity <= 0) return 0;
  if (!Number.isFinite(perContract) || perContract <= 0) return 0;
  return Math.max(0, Math.floor(capacity / perContract + 1e-9));
};

const roundedCashflow = (amount: number) =>
  Math.round((amount + Number.EPSILON) * 100) / 100;

/** Applies the same one-time, half-up premium rounding used by option execution. */
const affordableBuyContracts = (
  economicMaximum: number,
  availableCash: number,
  premiumPerContract: number,
) => {
  if (economicMaximum <= 0 || availableCash < 0) return 0;
  if (!Number.isFinite(premiumPerContract) || premiumPerContract < 0) return 0;
  if (premiumPerContract === 0) return economicMaximum;

  let low = 0;
  let high = economicMaximum;
  while (low < high) {
    const candidate = Math.ceil((low + high) / 2);
    if (roundedCashflow(premiumPerContract * candidate) <= availableCash) {
      low = candidate;
    } else {
      high = candidate - 1;
    }
  }
  return low;
};

/**
 * Calculates player-facing whole-contract maxima without entering the ordinary
 * quantity/leverage path. The backend remains authoritative at submission.
 */
export const calculateSimplifiedOptionMaxContracts = ({
  instrument,
  asset,
  session,
  player,
  assetsList,
  currenciesList,
}: Inputs): SimplifiedOptionMaxContracts => {
  const option = instrument.simplified_option;
  if (
    !option ||
    option.availability?.available !== true ||
    option.settlement_failed
  ) {
    return { ...noContracts };
  }

  const positionContracts = Number(option.contracts);
  const multiplier = Number(option.contract_multiplier);
  const normalizedSpot = Number(option.normalized_spot);
  if (
    !Number.isInteger(positionContracts) ||
    !Number.isFinite(multiplier) ||
    multiplier <= 0 ||
    (asset.underlying_kind !== 'BOND' &&
      (!Number.isFinite(normalizedSpot) || normalizedSpot <= 0))
  ) {
    return { ...noContracts };
  }

  const nativeExposurePerContract =
    asset.underlying_kind === 'BOND' ? multiplier : multiplier * normalizedSpot;
  const reportingExposurePerContract = Math.abs(
    cashExchange(
      asset.variable_currency,
      session.reporting_currency,
      nativeExposurePerContract,
      0,
      assetsList,
      currenciesList,
    ),
  );
  if (
    !Number.isFinite(reportingExposurePerContract) ||
    reportingExposurePerContract <= 0
  ) {
    return { ...noContracts };
  }

  const currentExposure =
    Math.abs(positionContracts) * reportingExposurePerContract;
  let addMaximum = 0;
  if (session.risk_approach === CASH_APPROACH) {
    const instrumentLimit = session.hard_limits
      ? Number(session.starting_cash_amount) * Number(instrument.pv_limit)
      : Number(session.starting_cash_amount);
    addMaximum = wholeContracts(
      instrumentLimit - currentExposure,
      reportingExposurePerContract,
    );
  } else {
    const riskPerContract =
      reportingExposurePerContract * Number(instrument.risk_weight);
    addMaximum = wholeContracts(
      Number(session.risk_weighted_limit) - Number(player.risk_weighted_value),
      riskPerContract,
    );
  }

  const wallet = player.wallets?.find(
    item => Number(item.currency) === Number(asset.variable_currency),
  );
  const availableCash = Number(wallet?.amount ?? 0);
  const premiumPerControlledUnit = Number(option.premium_per_controlled_unit);
  if (
    !Number.isFinite(availableCash) ||
    !Number.isFinite(premiumPerControlledUnit) ||
    premiumPerControlledUnit < 0
  ) {
    return { ...noContracts };
  }
  const premiumPerContract = premiumPerControlledUnit * multiplier;

  const buyEconomicMaximum =
    positionContracts < 0 ? Math.abs(positionContracts) : addMaximum;
  const buyMaximum = affordableBuyContracts(
    buyEconomicMaximum,
    availableCash,
    premiumPerContract,
  );
  const sellMaximum = positionContracts > 0 ? positionContracts : 0;
  const shortSellMaximum =
    positionContracts <= 0
      ? session.risk_approach === CASH_APPROACH
        ? Math.min(
            addMaximum,
            wholeContracts(
              Number(player.remaining_short_sell_amount_limit),
              reportingExposurePerContract,
            ),
          )
        : addMaximum
      : 0;

  return {
    [BUY]: buyMaximum,
    [SELL]: sellMaximum,
    [SHORT_SELL]: shortSellMaximum,
  };
};
